import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { Buildings } from "@phosphor-icons/react";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const from = location.state?.from || "/";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bienvenue !");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Erreur de connexion", { duration: 7000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto grid min-h-[80vh] max-w-6xl gap-8 px-4 py-10 md:grid-cols-2 md:px-8 md:py-16">
      <div className="hidden flex-col justify-between rounded-3xl bg-secondary p-10 text-secondary-foreground md:flex">
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary">
            <Buildings size={22} weight="duotone" />
          </div>
          <span className="font-display text-xl font-black">BODJE IMMO GUINÉE</span>
        </div>
        <div>
          <h2 className="font-display text-4xl font-black leading-tight">
            Ravi de vous revoir sur BODJE IMMO GUINÉE.
          </h2>
          <p className="mt-4 text-white/70">
            Connectez-vous pour gérer vos annonces, contacter les propriétaires et sauvegarder vos favoris.
          </p>
        </div>
        <div className="text-xs uppercase tracking-widest text-white/50">Conakry · République de Guinée</div>
      </div>

      <div className="flex flex-col justify-center">
        <div className="mb-8">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-primary">Connexion</span>
          <h1 className="mt-2 font-display text-3xl font-black md:text-4xl">Bon retour parmi nous</h1>
          <p className="mt-2 text-muted-foreground">
            Pas encore de compte ?{" "}
            <Link to="/register" className="font-bold text-primary hover:underline" data-testid="go-register">
              Créer un compte
            </Link>
          </p>
        </div>


        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider">
              Email
            </Label>
            <Input
              id="email"
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-2 h-12"
              placeholder="vous@exemple.com"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider">
              Mot de passe
            </Label>
            <Input
              id="password"
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-2 h-12"
              placeholder="••••••••"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="btn-pill h-12 w-full rounded-full bg-primary font-bold text-primary-foreground hover:bg-primary/90"
          >
            {loading ? "Connexion…" : "Se connecter"}
          </Button>
        </form>
      </div>
    </div>
  );
}
